import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import EmployeeDataService from "../../../services/Employee";
import { getUserAccess } from "../../../utils/authUtils";
import { HrmsPageShell } from "../../../components/Hrms/HrmsPageShell";
import { HrmsCard } from "../../../components/Hrms/HrmsCard";
import { HrmsStatCard } from "../../../components/Hrms/HrmsStatCard";

interface PendingApproval {
  _id: string;
  name?: string;
  email: string;
  type: string;
  start_date: string;
  end_date: string;
}

interface Flag {
  _id: string;
  email: string;
  raisedBy: string;
  reason: string;
  severity: string;
}

const COLORS = ["#308888", "#e2e8f0"];

export const AdminDashboard = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const toast = useToast();
  const { canViewAdminDashboard } = getUserAccess();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isLoading, setIsLoading] = useState(true);
  const [headcount, setHeadcount] = useState(0);
  const [presentToday, setPresentToday] = useState(0);
  const [attendancePercentToday, setAttendancePercentToday] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
  const [activeFlags, setActiveFlags] = useState<Flag[]>([]);

  const [spotlightForm, setSpotlightForm] = useState({
    employeeEmail: "",
    title: "Employee of the Quarter",
    message: "",
  });

  const fetchWidgets = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getAdminDashboardWidgets(token);
      if (res.data.status === "success" && res.data.data) {
        setHeadcount(res.data.data.headcount || 0);
        setPresentToday(res.data.data.presentToday || 0);
        setAttendancePercentToday(res.data.data.attendancePercentToday || 0);
        setPendingApprovals(res.data.data.pendingApprovals || []);
        setActiveFlags(res.data.data.activeFlags || []);
      }
    } catch (e: any) {
      console.error("Error fetching admin dashboard widgets:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetSpotlight = async () => {
    if (!spotlightForm.employeeEmail) {
      toast({
        position: "top",
        title: "Employee email is required",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    try {
      const res = await EmployeeDataService.setDashboardSpotlight(
        token,
        spotlightForm,
        userEmail,
      );
      if (res.data.status === "success") {
        toast({
          position: "top",
          title: "Spotlight updated",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        onClose();
      }
    } catch (e: any) {
      toast({
        position: "top",
        title: "Error",
        description: e.response?.data?.message || "Failed to update spotlight.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleResolveFlag = async (id: string) => {
    try {
      await EmployeeDataService.resolveDashboardFlag(token, id, userEmail);
      fetchWidgets();
    } catch (e: any) {
      console.error("Error resolving flag:", e);
    }
  };

  useEffect(() => {
    fetchWidgets();
  }, []);

  const attendanceData = [
    { name: "Present", value: presentToday },
    { name: "Absent", value: Math.max(0, headcount - presentToday) },
  ];

  return (
    <HrmsPageShell
      title="Admin Dashboard"
      sectionKey="dashboards"
      noPermission={!canViewAdminDashboard}
      isLoading={canViewAdminDashboard && isLoading}
      actions={
        canViewAdminDashboard ? (
          <Button
            size="sm"
            bg="#327171"
            color="white"
            _hover={{ bg: "#285e5e" }}
            onClick={onOpen}
          >
            Set Spotlight
          </Button>
        ) : undefined
      }
    >
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={6}>
        <HrmsStatCard label="Headcount" value={headcount} />
        <HrmsStatCard
          label="Present Today"
          value={`${presentToday} (${attendancePercentToday}%)`}
          accent="#2e7d32"
        />
        <HrmsStatCard
          label="Pending Approvals"
          value={pendingApprovals.length}
          accent="#fdb508"
        />
        <HrmsStatCard
          label="Active Flags"
          value={activeFlags.length}
          accent="#c62828"
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <HrmsCard title="Today's Org-Wide Attendance">
          <Box h="220px">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={attendanceData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={80}
                >
                  {attendanceData.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </HrmsCard>

        <HrmsCard title="Active Employee Flags">
          {activeFlags.length > 0 ? (
            activeFlags.map((flag) => (
              <Flex
                key={flag._id}
                justify="space-between"
                align="center"
                py={2}
                borderBottom="1px solid #eef2f2"
              >
                <Box>
                  <Text fontSize="13px" fontWeight="600" mb={0}>
                    {flag.email}
                  </Text>
                  <Text fontSize="12px" color="gray.500" mb={0}>
                    {flag.reason}
                  </Text>
                </Box>
                <Flex align="center" gap={2}>
                  <Badge
                    colorScheme={
                      flag.severity === "high"
                        ? "red"
                        : flag.severity === "low"
                          ? "gray"
                          : "orange"
                    }
                  >
                    {flag.severity}
                  </Badge>
                  <Button size="xs" onClick={() => handleResolveFlag(flag._id)}>
                    Resolve
                  </Button>
                </Flex>
              </Flex>
            ))
          ) : (
            <Text fontSize="13px" color="gray.500" mb={0}>
              No active flags.
            </Text>
          )}
        </HrmsCard>
      </SimpleGrid>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Set Employee Spotlight</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3} isRequired>
              <FormLabel>Employee Email</FormLabel>
              <Input
                value={spotlightForm.employeeEmail}
                onChange={(e) =>
                  setSpotlightForm({
                    ...spotlightForm,
                    employeeEmail: e.target.value,
                  })
                }
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Title</FormLabel>
              <Input
                value={spotlightForm.title}
                onChange={(e) =>
                  setSpotlightForm({ ...spotlightForm, title: e.target.value })
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>Message</FormLabel>
              <Textarea
                value={spotlightForm.message}
                onChange={(e) =>
                  setSpotlightForm({ ...spotlightForm, message: e.target.value })
                }
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg="#327171"
              color="white"
              _hover={{ bg: "#285e5e" }}
              onClick={handleSetSpotlight}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </HrmsPageShell>
  );
};
